import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useLocation, useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import { message } from 'antd';
import classNames from 'classnames';
import styles from './SearchCourse.module.less';

import { CONTEXT, MICROCONTEXT } from '../../config';
import Loading from '../../components/Loading/Loading';
import { getQueryString } from '../../utils/utils';
import Toast from '../../components/Toast/Toast';
import Dialog from '../../components/Dialog/Dialog';
import EduCard from '../../components/EduCard/EduCard';
import EduCardSearch from '../../components/EduCard/EduCardSearch';

import { getSearchCourse, getSearchCourseHot } from '../../actions/searchResult';
import { logging, logPostInfo } from '../../actions/log';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectSearchInfo = createSelector(
  state => state.search,
  search => search,
);
const selectSearchKw = createSelector(
  state => state.layout,
  layout => layout.searchKw,
);

const selectSearchResultObj = createSelector(
  state => state.searchResult,
  searchResult => searchResult,
);

export default memo(({ scrollNode, logInfo }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);

  const { searchParam, searchCourse, searchCount, searchCourseHot } = useSelector(
    selectSearchResultObj,
  );

  const keyword = getQueryString('keyword');

  // 浏览时长打桩
  const leaveTimeLog = useCallback(
    initTime => {
      const keyword = getQueryString('keyword');
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Search_Course`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            search_type: logInfoData.search_type,
            search_word: logInfoData.search_word || keyword,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
            keyword,
            searchParam,
          },
          param3: searchCourse.bizInfo,
        }),
      );
    },
    [logInfoData, searchParam, searchCourse],
  );
  // 统计页面浏览时长
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      leaveTimeLog(initTime);
    };
  }, []);

  /**
   * 当数据为activeTab数据，tab为activeTab，且请求的是第一页的数据
   * 上报请求完成的桩，当做一次搜索结果成功的上报
   */
  useEffect(() => {
    if (
      searchParam?.searchWord === searchCourse?.searchParam?.searchWord &&
      searchParam?.activeTab === 'course' &&
      searchParam?.coursePaySel === searchCourse?.searchParam?.coursePaySel &&
      searchCourse?.loading === 200
    ) {
      // 请求数据成功
      if (searchCourse?.dataList?.length > 0 && searchCourse?.dataList?.length <= 48) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult',
            label: '',
            param2: {
              source: 'Search_Course',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchCourse?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchCourse?.bizInfo,
          }),
        );
      }
      // 请求数据为空
      if (searchCourse?.dataList?.length === 0) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult_NoData',
            label: '',
            param2: {
              source: 'Search_Course',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchCourse?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchCourse?.bizInfo,
          }),
        );
      }
    }
  }, [searchCourse, searchParam]);

  // 开始请求推荐数据
  useEffect(() => {
    dispatch(getSearchCourseHot());
  }, []);

  // 当searchParam参数和  返回的数据中pageParam参数不一致的时候，就重新请求页面数据
  useEffect(() => {
    if (
      !(
        searchParam?.searchWord === searchCourse?.searchParam?.searchWord &&
        searchParam?.activeTab === searchCourse?.searchParam?.activeTab &&
        searchParam?.coursePaySel === searchCourse?.searchParam?.coursePaySel
      )
    ) {
      let params = {
        refresh: true,
        searchWord: getQueryString('keyword'),
        coursePaySel: searchParam?.coursePaySel || '0',
        algoBizInfo: searchCourse?.algoBizInfo || '',
      };
      // 如果切换搜索词
      if (searchParam?.searchWord !== searchCourse?.searchParam?.searchWord) {
        params.algoBizInfo = '';
      }

      // 传入相关数据（重新）请求数据
      dispatch(getSearchCourse(params));
    }
  }, [searchCourse, searchParam]);

  const loadMore = useCallback(() => {
    let params = {
      searchMode: '1', // searchMode仅仅在loadmore时传入  因为useEffect并不知道是否为点击搜索按钮的搜索
      refresh: false,
      searchWord: getQueryString('keyword'),
      coursePaySel: searchParam?.coursePaySel || '0',
      algoBizInfo: searchCourse?.algoBizInfo || '',
    };
    // 传入相关数据（继续）请求数据
    dispatch(getSearchCourse(params));
  }, [searchCourse, searchParam]);

  const handleSearchOut = () => {
    window.open(`https://www.baidu.com/s?wd=${keyword}`);
    dispatch(
      logging({
        category: 'C',
        action: 'Search_To_Browser',
        label: '',
        param2: {
          source: 'Search_Course',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          search_type: logInfoData.search_type,
          search_word: logInfoData.search_word || keyword,
          keyword,
        },
      }),
    );
  };

  if (
    !searchCourse ||
    !searchCourse?.loading ||
    searchCourse?.loading !== 200 ||
    searchCourse?.searchParam?.searchWord !== getQueryString('keyword')
  ) {
    return (
      <div className={styles.s_loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.s_course}>
      <InfiniteScroll
        pageStart={1}
        loadMore={loadMore}
        hasMore={searchCourse?.dataList?.length < searchCount?.coursesCount}
        loader={
          <div className="loader" key={0}>
            内容加载中...
          </div>
        }
        initialLoad={true}
        useWindow={false}
        getScrollParent={() => scrollNode}
        threshold={800}
      >
        {searchCourse?.dataList?.length > 0 && (
          <div className={`s_list`}>
            {searchCourse?.dataList?.map((item, index) => {
              return (
                <EduCardSearch
                  key={index}
                  data={item}
                  tag="left"
                  log={{
                    source: `Search_Course`,
                  }}
                  hasDelete={false}
                  type="reading"
                />
              );
            })}
          </div>
        )}
        {searchCourse?.dataList?.length > 0 &&
          searchCourse?.dataList?.length >= searchCount?.coursesCount && (
            <div className={styles.search_out} onClick={handleSearchOut}>
              没找到满意结果？试试{' '}
              <span className={styles.search_out_text}>
                百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
              </span>
            </div>
          )}
        {searchCourse?.dataList?.length === 0 && (
          <div className={styles.Nosearchresult}>
            <div className="NosearchresultBox">
              <div className="noresultImg" />
              <div className="mainTextWrap">
                <div className="mainText" onClick={handleSearchOut}>
                  没找到匹配结果。试试&nbsp;
                  <span className={styles.search_out_text}>
                    百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
                  </span>
                  <br />{' '}
                </div>
              </div>
            </div>
          </div>
        )}

        {((searchCourse?.dataList?.length > 0 &&
          searchCourse?.dataList?.length >= searchCount?.coursesCount) ||
          !searchCourse?.dataList?.length ||
          searchCourse?.dataList?.length === 0) &&
          searchCourseHot?.data?.length > 0 && (
            <div className={styles.s_reco}>
              <div className={'s_reco_header'}>
                <div className={'s_reco_header_title'}>看看这些热门课程</div>
                <div
                  className={'s_reco_header_more'}
                  onClick={() => {
                    dispatch(
                      logging({
                        category: 'C',
                        action: 'Search_Course_Reco_More',
                        label: '',
                        param2: {
                          source: 'Search_Course_Reco',
                          source_p: logInfoData.source_p,
                          source_pp: logInfoData.source_pp,
                          search_type: logInfoData.search_type,
                          search_word: logInfoData.search_word || keyword,
                          keyword,
                        },
                      }),
                    );
                    dispatch(
                      logPostInfo({
                        source: 'Search_Course_Reco',
                        source_p: logInfoData.source_p,
                        source_pp: logInfoData.source_pp,
                      }),
                    );
                    history.push(`${CONTEXT}/education/`);
                  }}
                >
                  查看更多
                </div>
              </div>
              <div className={'s_reco_list'}>
                {searchCourseHot?.data?.slice(0, 24).map((item, index) => {
                  return (
                    <EduCard
                      key={index}
                      data={item}
                      tag="left"
                      log={{
                        source: `Search_Course_Reco`,
                      }}
                      hasDelete={false}
                      type="reading"
                    />
                  );
                })}
              </div>
            </div>
          )}
      </InfiniteScroll>
    </div>
  );
});
